import { useCallback, useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useLocation } from 'react-router-dom';
import { ResponseError, responseErrorsResolver } from '@ui/src';
import queryString from 'query-string';
import { useAppDispatch } from '@store';
import { TransactionTypeProps, OrganizationType } from '../../types';
import { CustomerDocument, CustomerDocumentStatus } from '../types';
import {
  useChangeCustomerDocumentsCategoryMutation,
  useChangeCustomerDocumentsStatusMutation,
  useChangeDocumentExpirationDateMutation,
  useDeleteCustomerDocumentMutation,
  useLazyGetCustomerDocumentFileQuery,
  useLazyGetCustomerDocumentQuery,
  useLazyGetCustomerDocumentsQuery,
  useLazyGetCustomerOrganizationDocumentsStatsQuery,
  useLazyGetTransactionInDocumentQuery,
  useRenameCustomerDocumentMutation,
} from '../../../customers/store/services/customers';
import { useAddCustomersCommentMutation } from '../../../../store/documents/services';
import {
  clearDocumentState,
  deleteIsSuccess,
  onDocumentRaname,
  onDocumentsMoveById,
  setNeedToReloadCategoryId,
} from '../../store/slices/documents/documents';

import ROUTES from '../../../../routes';

const useDocumentInfo = ({
  customerType,
  documentId,
  customerId,
  info,
  customersTransactionTypes,
}: {
  customerType: OrganizationType;
  documentId?: string;
  customerId?: string;
  info?: CustomerDocument;
  customersTransactionTypes?: TransactionTypeProps[];
}) => {
  const [panelIsOpen, setPanelIsOpen] = useState(false);
  const [deletePopupVisible, setDeletePopupVisible] = useState(false);
  const [imgLink, setImgLink] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<
    CustomerDocumentStatus | undefined
  >(undefined);
  const [changeDateError, setChangeDateError] = useState('');
  const [documentTransactionType, setDocumentTransactionType] = useState('');
  const [documentName, setDocumentName] = useState('');
  const [nameError, setNameError] = useState('');

  const textareaRef = useRef<HTMLTextAreaElement | null>(null);

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { search, hash } = useLocation();

  const {
    setError,
    formState: { errors },
  } = useForm();

  const [getDocument, { isLoading }] = useLazyGetCustomerDocumentQuery();
  const [getDocumentFile, { isLoading: fileIsLoading, isFetching }] =
    useLazyGetCustomerDocumentFileQuery();
  const [deleteDocument, { isLoading: deleteInProgress }] =
    useDeleteCustomerDocumentMutation();
  const [
    changeCustomerDocumentsStatus,
    { isLoading: statusChangeInProgress, isSuccess },
  ] = useChangeCustomerDocumentsStatusMutation();
  const [changeCustomerDocumentsCategory, { isLoading: moveInProgress }] =
    useChangeCustomerDocumentsCategoryMutation();
  const [addComment, { isLoading: commentInProgress }] =
    useAddCustomersCommentMutation();
  const [changeDocumentExpDate, { isLoading: updateInProgress }] =
    useChangeDocumentExpirationDateMutation();
  const [getTransaction] = useLazyGetTransactionInDocumentQuery();
  const [rename, { isLoading: documentRenameInProgress }] =
    useRenameCustomerDocumentMutation();
  const [getCustomerDocuments] = useLazyGetCustomerDocumentsQuery();
  const [getStats] = useLazyGetCustomerOrganizationDocumentsStatsQuery();

  const addCommentAction = (text: string) => {
    addComment({
      id: documentId as string,
      body: {
        text,
      },
    });
  };

  const onRename = async () => {
    if (documentName?.length < 3) {
      setNameError('name must be longer than or equal to 3 characters');
    } else {
      setNameError('');

      const response = (await rename({
        id: customerId as string,
        documentId: info?.id as string,
        body: {
          name: documentName,
        },
      })) as { error?: ResponseError };

      if (response && !response.error) {
        dispatch(
          onDocumentRaname({
            name: documentName,
            type: info?.documentCategoryId as string,
            documentId: info?.id as string,
          }),
        );
      } else if (response.error) {
        responseErrorsResolver({
          errors: response.error,
          setError,
        });
      }
    }
  };

  const getTransactionAction = useCallback(async (transactionId: string) => {
    const response = (await getTransaction(transactionId)) as {
      data: { data: { typeId: string } };
    };

    if (response && response.data && response.data.data) {
      const currentType = customersTransactionTypes?.find(
        (item) => item.id === response.data.data.typeId,
      );

      if (currentType) {
        setDocumentTransactionType(currentType.name);
      }
    }
  }, []);

  let link: string;

  if (customerType === OrganizationType.INDIVIDUAL) {
    link = generatePath(ROUTES.CUSTOMERS_INDIVIDUAL_DOCUMENTS, {
      customerId: customerId as string,
    });
  } else if (customerType === OrganizationType.CORPORATE) {
    link = generatePath(ROUTES.CUSTOMERS_CORPORATE_DOCUMENTS, {
      customerId: customerId as string,
    });
  }

  const handleClose = useCallback(() => {
    setPanelIsOpen(false);
    setDocumentName('');
    setNameError('');

    setTimeout(() => {
      navigate(`${link}${search || ''}`);
    }, 100);
  }, []);

  const onDownload = () => {
    if (imgLink) {
      const hiddenElement = document.createElement('a');
      // @ts-ignore
      hiddenElement.href = imgLink;
      hiddenElement.target = '_blank';
      hiddenElement.download = `${info?.name}`;
      hiddenElement.click();
    }
  };

  const onChangeDocumentExpDate = async (date: string) => {
    const response = (await changeDocumentExpDate({
      id: documentId as string,
      body: {
        id: documentId as string,
        expirationDate: date,
      },
    })) as { error?: ResponseError | { data: { message: string } } };

    if (response.error) {
      if (Array.isArray(response?.error?.data?.message)) {
        if (response?.error?.data?.message[0]?.property === 'coreError') {
          setChangeDateError(
            response?.error?.data?.message[0]?.value as string,
          );
        }
      } else if (typeof response?.error?.data?.message === 'string') {
        setChangeDateError(response?.error?.data?.message);
      } else {
        setChangeDateError('Internal Server Error. Try Again');
      }
    }
  };

  const onDelete = async () => {
    const response = await deleteDocument({
      id: customerId as string,
      documentId: info?.id as string,
    });

    if (response) {
      getStats(customerId as string);

      dispatch(deleteIsSuccess({ ids: [info?.id as string], id: info?.id }));

      setDeletePopupVisible(false);

      handleClose();
    }
  };

  const changeCustomerDocumentsCategoryAction = async (
    documentCategoryId: string,
  ) => {
    const body: {
      ids: string[];
      documentCategoryId?: string | undefined;
    } = {
      ids: [documentId as string],
      documentCategoryId,
    };

    const response = (await changeCustomerDocumentsCategory({
      id: customerId as string,
      body,
    })) as { error?: any };

    if (response && !response.error) {
      getStats(customerId as string);

      let sort = 'asc';
      let sortProp = 'createdAt';

      const movedDocument: {
        documentCategoryId: string;
        ids: string[];
        type?: string;
        id: string;
      } = {
        documentCategoryId,
        ids: [documentId as string],
        id: documentId as string,
      };

      if (search) {
        const parsed = queryString.parse(search);

        if (parsed.sort) {
          sort = parsed.sort as string;
        }

        if (parsed.sortProp) {
          sortProp = parsed.sortProp as string;
        }

        if (parsed.documentCategoryId) {
          movedDocument.type = parsed.documentCategoryId as string;
        } else {
          movedDocument.type = 'all';
        }
      } else {
        movedDocument.type = 'all';
      }

      dispatch(onDocumentsMoveById(movedDocument));

      if (
        movedDocument.type &&
        movedDocument.type === movedDocument.documentCategoryId
      ) {
        if (movedDocument.type !== 'all') {
          getCustomerDocuments({
            id: customerId as string,
            categoryId: movedDocument.documentCategoryId,
            take: '20',
            reload: true,
            sort,
            sortProp,
          });
        }
      } else {
        dispatch(setNeedToReloadCategoryId(movedDocument.documentCategoryId));
      }
    }
  };

  const getDocumentFileAction = async () => {
    const response = await getDocumentFile({
      id: customerId as string,
      documentId: documentId as string,
    });

    if (response.data) {
      const url = window.URL || window.webkitURL;

      // @ts-ignore
      const blob = url.createObjectURL(response.data);

      setImgLink(blob);
    }
  };

  useEffect(() => {
    if (info && !documentName) {
      setDocumentName(info.name);
    }
  }, [info, documentName]);

  useEffect(() => {
    if (isSuccess) {
      setSelectedStatus(undefined);
    }
  }, [isSuccess]);

  useEffect(() => {
    if (!info && documentId && customerId) {
      getDocument({ id: customerId, documentId });
      getDocumentFileAction();
    }
  }, [documentId, info, customerId]);

  useEffect(() => {
    setPanelIsOpen(true);

    return () => {
      dispatch(clearDocumentState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (hash) {
      setTimeout(() => {
        textareaRef?.current?.focus();
      }, 100);
    }
  }, [hash, textareaRef]);

  useEffect(() => {
    if (info && info?.transactionId) {
      getTransactionAction(info?.transactionId);
    }
  }, [info]);

  return {
    documentName,
    setDeletePopupVisible,
    onDelete,
    deleteInProgress,
    deletePopupVisible,
    panelIsOpen,
    handleClose,
    documentRenameInProgress,
    onRename,
    statusChangeInProgress,
    selectedStatus,
    setSelectedStatus,
    changeCustomerDocumentsStatus,
    isLoading,
    moveInProgress,
    changeCustomerDocumentsCategoryAction,
    imgLink,
    fileIsLoading,
    isFetching,
    setDocumentName,
    commentInProgress,
    addCommentAction,
    onDownload,
    textareaRef,
    onChangeDocumentExpDate,
    changeDateError,
    updateInProgress,
    documentTransactionType,
    nameError,
    errors,
  };
};

export default useDocumentInfo;
